import { createSelector } from 'reselect';
// ---- external imports ----

import { PRODUCT_TYPES } from '~/lib/constants';
import { getSelectedCharacterSlug } from '../character/selectors';
import { CHARACTERS_SLUG } from '../../lib/constants';
// ---- internal imports ----

export function areProductsLoaded(state) {
    return state.products.isLoaded;
}

export function getCouch(state) {
    return state.products.couch;
}

export function getChair(state) {
    return state.products.chair;
}

export function getTable(state) {
    return state.products.table;
}

export function getProduct(state, productType) {
    return state.products[productType];
}

export function hasProductInStock(state, productType) {
    return getProduct(state, productType)?.amount > 0;
}

export function getOriginalPurchasingPrice(state) {
    const purhcasePrices = {};

    Object.values(PRODUCT_TYPES).forEach(
        productType => (purhcasePrices[productType] = getOriginalPurchasingPriceForProduct(state, productType)),
    );

    return purhcasePrices;
}

export function getCharacterPurchasePrice(state) {
    const characterSlug = getSelectedCharacterSlug(state);
    const purhcasePrices = {};
    const priceFactor = characterSlug === CHARACTERS_SLUG.TOOHA ?  0.85 : 1;

    Object.values(PRODUCT_TYPES).forEach(
        (productType) => {
            const price = Math.round(priceFactor * getOriginalPurchasingPriceForProduct(state, productType));
            purhcasePrices[productType] = price;
        },
    );

    return purhcasePrices;
}

export function hasProductsInStock(state) {
    return state.products.table.amount + state.products.couch.amount + state.products.chair.amount > 0;
}

export function getSellingPriceFor(state, productType) {
    return state.products[productType]?.sellingPrice;
}

export function getInitialSellingPriceFor(state, productType) {
    return state.products[productType]?.initialSellingPrice;
}

export function getOriginalPurchasingPriceForProduct(state, productType) {
    return state.products[productType]?.originalPurchasingPrice;
}

export function getSellingPrices(state) {
    const sellingPrices = {};

    Object.values(PRODUCT_TYPES).forEach(
        productType => (sellingPrices[productType] = getSellingPriceFor(state, productType)),
    );

    return sellingPrices;
}

export function getProductsCountInStock(state) {
    const productsCount = getCouch(state).amount + getChair(state).amount + getTable(state).amount;
    return isNaN(productsCount) ? 0 : parseInt(productsCount);
}

export function getInStockProductsValue(state) {
    const productsValue =
        getCouch(state).amount * getCouch(state).purchasingPrice +
        getChair(state).amount * getChair(state).purchasingPrice +
        getTable(state).amount * getTable(state).purchasingPrice;
    return parseInt(productsValue);
}

export function getProductsSalesForCurrMonth(state) {
    return { couch: [getChair(state)], chair: [getChair(state)], table: [getTable(state)] };
}

export function getSalesDataForCurrMonth(state) {
    return [
        state.products.productSalesForCurrMonth.couch.amount,
        state.products.productSalesForCurrMonth.chair.amount,
        state.products.productSalesForCurrMonth.table.amount,
    ];
}
export function getProductSalesForCurrMonth(state) {
    return state.products.productSalesForCurrMonth;
}

export const getMemorizedSalesDataForCurrMonth = createSelector(getProductSalesForCurrMonth, productSales => {
    return [productSales.couch.amount, productSales.chair.amount, productSales.table.amount];
});

export function getTotalSalesCountForCurrMonth(state) {
    return (
        state.products.productSalesForCurrMonth.couch.amount +
        state.products.productSalesForCurrMonth.chair.amount +
        state.products.productSalesForCurrMonth.table.amount
    );
}

export function getTotalSalesRevenuesForCurrMonth(state) {
    return (
        state.products.productSalesForCurrMonth.couch.amount * getCouch(state).sellingPrice +
        state.products.productSalesForCurrMonth.chair.amount * getChair(state).sellingPrice +
        state.products.productSalesForCurrMonth.table.amount * getTable(state).sellingPrice
    );
}

export function getMaxProductInStock(state) {
    return Math.max(state.products.table.amount, state.products.couch.amount, state.products.chair.amount);
}

export function hasLowStock(state) {
    for(let productType of Object.values(PRODUCT_TYPES)) {
        if(getProduct(state, productType).amount <= 1) {
            return true;
        }
    }
    return false;
}
